import { When } from 'react-if';
import {
  IPaymentPlanConfig,
  IPaymentPlanInstalmentItem,
  IPaymentPlanTerms,
  PaymentPlanFirstPaymentDate,
  PaymentPlanFrequency,
  PaymentPlanPreview,
  PaymentPlanSurchargeUnit,
  paymentPlanUtils,
  toCents,
  toDecimal,
} from 'shared';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';

import { Modal } from '../Atoms/Modal';
import { InputTextAddon } from '../Atoms/InputTextAddon';
import { CurrencyInput } from '../Atoms/CurrencyInput';
import { Paragraph } from '../Atoms/Typography/Paragraph';
import { Button } from '../Atoms/Button';
import { Badge } from '../Atoms/Badge';
import { RadioCards } from '../Atoms/RadioCards';
import { Select } from '../Atoms/Select';
import { LoadingOverlay } from '../../main/components/Molecules/LoadingOverlay';
import { AccountSelector } from '../../main/components/Settings/Policies/PolicyForm/AccountSelector';

interface CreatePaymentPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  terms: IPaymentPlanTerms;
  setTerms: (terms: IPaymentPlanTerms) => void;
  onCreate: () => Promise<void>;
  config?: IPaymentPlanConfig;
  preview?: PaymentPlanPreview;
  previewError?: string | null;
  onPreview: (terms: IPaymentPlanTerms) => Promise<void>;
  previewLoading: boolean;
  mode: `portal` | `app`;
  adjustedInstalments?: IPaymentPlanInstalmentItem[];
  onAdjustedInstalmentsChange?: (instalments: IPaymentPlanInstalmentItem[]) => void;
}

const frequencyTypeOptions = [
  { value: PaymentPlanFrequency.WEEKLY, label: `Weekly` },
  { value: PaymentPlanFrequency.FORTNIGHTLY, label: `Fortnightly` },
  { value: PaymentPlanFrequency.FOURWEEKLY, label: `4 Weekly` },
  { value: PaymentPlanFrequency.MONTHLY, label: `Monthly` },
  { value: PaymentPlanFrequency.BIMONTHLY, label: `Bi-monthly` },
  { value: PaymentPlanFrequency.QUARTERLY, label: `Quarterly` },
];

export function getFrequencyTypeLabel(value: IPaymentPlanConfig[`frequencyType`]): string {
  switch (value) {
  case `weekly`:
    return `Weekly`;
  case `fortnightly`:
    return `Fortnightly`;
  case `fourweekly`:
    return `4 Weekly`;
  case `bimonthly`:
    return `Bi-monthly`;
  case `quarterly`:
    return `Quarterly`;
  case `monthly`:
  default:
    return `Monthly`;
  }
}

export function CreatePaymentPlanModal({
  isOpen,
  onClose,
  isLoading,
  terms,
  setTerms,
  onCreate,
  config,
  preview,
  previewError,
  onPreview,
  previewLoading,
  mode,
  adjustedInstalments,
  onAdjustedInstalmentsChange,
}: CreatePaymentPlanModalProps) {
  const pendingFocusRef = useRef<{ index: number; field: `date` | `amount` | `percentage` } | null>(null);
  const [editingPercentage, setEditingPercentage] = useState<{ index: number; value: string } | null>(null);
  const validation = paymentPlanUtils.termsAreValid(terms, config);
  const invalid = !validation.valid;
  const appAdjustedInstalments = adjustedInstalments || [];
  const portalPaymentCountOptions = useMemo(() => {
    const max = Math.max(2, config?.maxNumberOfPayments ?? 12);
    const values: number[] = [];
    for (let i = 2; i <= max; i++) {
      values.push(i);
    }

    return values;
  }, [config?.maxNumberOfPayments]);

  function handleChange(e: { value: string | number; name: string }) {
    const { name, value } = e;
    const numeric = typeof value === `number` ? value : Number(value);

    const next: IPaymentPlanTerms = {
      ...terms,
      [name]: Number.isFinite(numeric) ? numeric : 0,
    } as IPaymentPlanTerms;

    setTerms(next);
  }

  function handleStringChange(e: { value: string; name: string }) {
    const { name, value } = e;

    setTerms({
      ...terms,
      [name]: value,
    } as IPaymentPlanTerms);
  }

  function handleFrequencyTypeSelectChange(e: { value: string }) {
    const frequencyType = e.value as PaymentPlanFrequency;

    setTerms({
      ...terms,
      frequencyType,
    });
  }

  // Surcharge handlers (app mode only)
  function handleSurchargeUnitChange({ value }: { value: string }) {
    const unit = value === `` ? null : (value as PaymentPlanSurchargeUnit);

    setTerms({
      ...terms,
      surchargeUnit: unit,
    });
  }

  function formatDurationText(frequencyType?: PaymentPlanFrequency) {
    const interval = paymentPlanUtils.getFrequencyTypeInterval(frequencyType || PaymentPlanFrequency.MONTHLY);
    const frequency = interval.frequency;
    const u = interval.frequencyUnit;
    const unitLabel = frequency === 1
      ? (u === `days` ? `day` : u === `weeks` ? `week` : `month`)
      : (u === `days` ? `days` : u === `weeks` ? `weeks` : `months`);

    return frequency === 1 ? `1 ${unitLabel}` : `${frequency} ${unitLabel}`;
  }

  // Lock frequency interval to config when provided
  useEffect(() => {
    if (!config) return;
    if (terms.frequencyType !== config.frequencyType) {
      setTerms({
        ...terms,
        frequencyType: config.frequencyType,
      });
    }
  }, [config?.frequencyType]);

  const selectedAppFrequencyType = useMemo(() => {
    return terms.frequencyType || PaymentPlanFrequency.MONTHLY;
  }, [terms.frequencyType]);

  // Build first payment options and filter by config
  const allFirstPaymentOptions = useMemo(() => ([
    { value: `now`, title: `Start today`, description: `First payment will be due today` },
    { value: `dueDate`, title: `On due date`, description: `First payment due on the invoice due date` },
    { value: `nowPlusFrequency`, title: `After first interval`, description: `First payment due in ${formatDurationText(terms.frequencyType) || `the first interval`}` },
  ]), [terms.frequencyType]);

  const filteredFirstPaymentOptions = useMemo(() => {
    if (!config) return allFirstPaymentOptions;
    const allowed = new Set(config.firstPaymentDates);

    return allFirstPaymentOptions.filter(o => allowed.has(o.value as PaymentPlanFirstPaymentDate));
  }, [config?.firstPaymentDates, allFirstPaymentOptions]);

  // Ensure firstPaymentDate is valid per config; if only one, set it
  useEffect(() => {
    if (!filteredFirstPaymentOptions.length) return;
    const allowedValues = new Set(filteredFirstPaymentOptions.map(o => o.value));
    if (!allowedValues.has((terms.firstPaymentDate ?? `now`))) {
      setTerms({
        ...terms,
        firstPaymentDate: filteredFirstPaymentOptions[0].value as PaymentPlanFirstPaymentDate,
      });
    }
  }, [config?.firstPaymentDates, filteredFirstPaymentOptions]);

  function updateAdjustedInstalment(
    index: number,
    update: Partial<IPaymentPlanInstalmentItem>,
    fieldToRefocus?: `date` | `amount` | `percentage`,
  ) {
    if (!onAdjustedInstalmentsChange || mode !== `app`) {
      return;
    }

    const nextInstalments = appAdjustedInstalments.map((item, currentIndex) => {
      if (currentIndex !== index) {
        return item;
      }

      return {
        ...item,
        ...update,
      };
    });
    const updatedInstalment = nextInstalments[index];

    const shouldResort = typeof update.dueDate === `string` && /^\d{4}-\d{2}-\d{2}$/.test(update.dueDate);
    const sortedInstalments = shouldResort
      ? [...nextInstalments].sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      : nextInstalments;
    const nextIndex = shouldResort ? sortedInstalments.findIndex(i => i === updatedInstalment) : index;
    if (fieldToRefocus && nextIndex >= 0 && shouldResort) {
      pendingFocusRef.current = {
        index: nextIndex,
        field: fieldToRefocus,
      };
    }

    onAdjustedInstalmentsChange(sortedInstalments);
  }

  useEffect(() => {
    const pendingFocus = pendingFocusRef.current;

    if (!pendingFocus || mode !== `app`) {
      return;
    }

    pendingFocusRef.current = null;

    requestAnimationFrame(() => {
      const target = document.querySelector<HTMLInputElement>(
        `[data-adjusted-instalment-field="${pendingFocus.field}-${pendingFocus.index}"]`,
      );

      target?.focus();
    });
  }, [appAdjustedInstalments, mode]);

  const hasInvalidAdjustedInstalments = mode === `app` && !!preview && (
    appAdjustedInstalments.length !== (preview.instalments?.length || 0)
    || appAdjustedInstalments.some(instalment => !instalment?.dueDate || !instalment?.amountCents || instalment.amountCents < 1)
  );
  const goalInstalmentTotalCents = useMemo(() => {
    if (!preview?.instalments?.length) {
      return 0;
    }

    return preview.instalments.reduce((sum, instalment) => sum + instalment.amountCents, 0);
  }, [preview?.instalments]);
  const currentInstalmentTotalCents = useMemo(() => {
    if (!preview) {
      return 0;
    }

    if (mode === `app`) {
      return appAdjustedInstalments.reduce((sum, instalment) => sum + (instalment?.amountCents || 0), 0);
    }

    return goalInstalmentTotalCents;
  }, [appAdjustedInstalments, goalInstalmentTotalCents, mode, preview]);
  const instalmentTotalDeltaCents = currentInstalmentTotalCents - goalInstalmentTotalCents;
  const hasInstalmentTotalMismatch = mode === `app` && !!preview && instalmentTotalDeltaCents !== 0;
  const currentInstalmentTotalPercentage = goalInstalmentTotalCents > 0
    ? (currentInstalmentTotalCents / goalInstalmentTotalCents) * 100
    : 0;
  const goalInstalmentTotalPercentage = goalInstalmentTotalCents > 0 ? 100 : 0;
  const instalmentTotalDeltaPercentage = currentInstalmentTotalPercentage - goalInstalmentTotalPercentage;

  return (
    <Modal
      title={
        <span className={ `flex items-center gap-2` }>
          <span>{ `Create Payment Plan` }</span>
          <When condition={ mode === `app` }>
            <Tooltip title={ `Payment plan parameters can be set to any values in admin mode` }>
              <span>
                <Badge
                  color={ `purple` }
                  message={ `Admin` }
                />
              </span>
            </Tooltip>
          </When>
        </span>
      }
      open={ isOpen }
      setOpen={ onClose }
      width={ `lg` }
    >
      <div className={ `space-y-5` }>
        <Paragraph variant={ `secondary` }
          className={ `text-sm text-gray-500` }>
          { `Set up the basic terms for this payment plan.` }
        </Paragraph>

        <When condition={ mode === `portal` && !!config }>
          <div className={ `rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3` }>
            <div className={ `flex items-center gap-2 whitespace-nowrap overflow-x-auto text-sm text-gray-700` }>
              <span>{ `Split invoice into` }</span>
              <TextField
                select
                size={ `small` }
                name={ `numberOfPayments` }
                value={ terms.numberOfPayments ?? `` }
                onChange={ e => handleChange({ name: `numberOfPayments`, value: e.target.value }) }
                sx={ {
                  width: 92,
                  '& .MuiOutlinedInput-root': { backgroundColor: `background.paper` },
                  '& .MuiInputBase-input': { py: 0.75 },
                } }
              >
                {
                  portalPaymentCountOptions.map(value => (
                    <MenuItem
                      key={ value }
                      value={ value }
                    >
                      { value }
                    </MenuItem>
                  ))
                }
              </TextField>
              <span>{ `${getFrequencyTypeLabel(config?.frequencyType || PaymentPlanFrequency.MONTHLY).toLowerCase()} payments` }</span>
            </div>
          </div>
        </When>
        <When condition={ mode === `app` || !config }>
          <div className={ `rounded-xl border border-gray-200 bg-gray-50/80 p-4` }>
            <div className={ `flex items-center gap-2 whitespace-nowrap overflow-x-auto text-sm text-gray-700` }>
              <span>{ `Split invoice into` }</span>
              <TextField
                size={ `small` }
                type={ `number` }
                name={ `numberOfPayments` }
                value={ terms.numberOfPayments ?? `` }
                onChange={ e => handleChange({ name: `numberOfPayments`, value: e.target.value }) }
                slotProps={ { htmlInput: { min: 2, max: 400 } } }
                sx={ {
                  width: 92,
                  '& .MuiOutlinedInput-root': { backgroundColor: `background.paper` },
                  '& .MuiInputBase-input': { py: 0.75 },
                } }
              />
              <TextField
                select
                size={ `small` }
                name={ `frequencyType` }
                value={ selectedAppFrequencyType }
                onChange={ e => handleFrequencyTypeSelectChange({ value: e.target.value }) }
                sx={ {
                  width: 170,
                  '& .MuiOutlinedInput-root': { backgroundColor: `background.paper` },
                  '& .MuiInputBase-input': { py: 0.75 },
                } }
              >
                {
                  frequencyTypeOptions.map(option => (
                    <MenuItem
                      key={ option.value }
                      value={ option.value }
                    >
                      { option.label }
                    </MenuItem>
                  ))
                }
              </TextField>
              <span>{ `payments` }</span>
            </div>
          </div>
        </When>

        <When condition={ mode === `app` }>
          <div>
            <Select
              label={ `Surcharge` }
              name={ `surchargeUnit` }
              options={ [
                { label: `Disabled`, value: `` },
                { label: `Percent`, value: PaymentPlanSurchargeUnit.Percent },
                { label: `Fixed amount`, value: PaymentPlanSurchargeUnit.FixedAmountCents },
              ] }
              selected={ terms.surchargeUnit ?? `` }
              onChange={ handleSurchargeUnitChange }
              helpIcon={ `Optional surcharge applied to the invoice when payment plan created.` }
            />
            <When condition={ !!terms.surchargeUnit }>
              <div className={ `mt-2 ml-2 pl-3 border-l border-gray-200 max-w-[200px]` }>
                <When condition={ terms.surchargeUnit === PaymentPlanSurchargeUnit.Percent }>
                  <InputTextAddon
                    label={ `Surcharge Percent` }
                    name={ `surchargePercent` }
                    value={ terms.surchargePercent || 0 }
                    addOnText={ `%` }
                    onChange={ handleStringChange }
                    helpIcon={ `Percent of invoice total to be added to the invoice` }
                  />
                </When>
                <When condition={ terms.surchargeUnit === PaymentPlanSurchargeUnit.FixedAmountCents }>
                  <CurrencyInput
                    label={ `Surcharge Amount` }
                    name={ `surchargeCents` }
                    value={ terms.surchargeCents || 0 }
                    onChange={ handleChange }
                    helpIcon={ `Fixed surcharge amount. This is the total amount for ALL installments.` }
                  />
                </When>

                <AccountSelector
                  label={ `Surcharge Revenue Account` }
                  name={ `externalSurchargeAccountCode` }
                  value={ terms.externalSurchargeAccountCode || null }
                  onChange={ handleStringChange }
                  description={ `Select the revenue account to record surcharge income against.` }
                />
              </div>
            </When>
          </div>
        </When>

        <When condition={ (filteredFirstPaymentOptions?.length || 0) > 1 }>
          <div className={ `rounded-xl border border-gray-200 bg-white p-4` }>
            <RadioCards
              label={ `First payment due date` }
              name={ `firstPaymentDate` }
              selected={ terms.firstPaymentDate ?? `now` }
              onChange={ handleStringChange }
              cols={ 3 }
              options={ filteredFirstPaymentOptions }
            />
          </div>
        </When>

        <div className={ `space-y-1` }>
          <When condition={ invalid }>
            <Paragraph variant={ `error` }
              size={ `sm` }>
              { validation.reason }
            </Paragraph>
          </When>
          <When condition={ !!previewError }>
            <Paragraph variant={ `error` }
              size={ `sm` }>
              { previewError }
            </Paragraph>
          </When>
        </div>

        <When condition={ !!preview || previewLoading }>
          <LoadingOverlay
            loading={ previewLoading }
          >
            <div className={ `rounded-xl border border-gray-200 bg-white p-4` }>
              <Paragraph className={ `mb-2 text-sm font-semibold text-gray-900` }>
                { `Preview` }
              </Paragraph>
              <div className={ `rounded-lg border border-gray-200 divide-y overflow-hidden` }>
                {
                  preview?.instalments?.map((instalment, idx) => {
                    const currentInstalment = mode === `app` ? appAdjustedInstalments[idx] || instalment : instalment;
                    const instalmentPercentage = goalInstalmentTotalCents > 0
                      ? (currentInstalment.amountCents / goalInstalmentTotalCents) * 100
                      : 0;
                    const minPercentage = 0.01;
                    const percentageValue = editingPercentage?.index === idx
                      ? editingPercentage.value
                      : (Number.isFinite(instalmentPercentage) ? instalmentPercentage.toFixed(2) : ``);

                    return (
                      <div
                        key={ `${instalment.dueDate}-${idx}` }
                        className={ `flex items-center justify-between px-3 py-2.5 text-sm odd:bg-gray-50/60` }
                      >
                        <When condition={ mode === `app` }>
                          <div className={ `grid w-full grid-cols-[120px_165px_130px_96px] items-center gap-2` }>
                            <span className={ `text-gray-600` }>{ `Installment ${idx + 1}` }</span>
                            <TextField
                              type={ `date` }
                              size={ `small` }
                              value={ currentInstalment?.dueDate || `` }
                              onChange={ e => updateAdjustedInstalment(idx, { dueDate: e.target.value }, `date`) }
                              slotProps={ { htmlInput: { 'data-adjusted-instalment-field': `date-${idx}` } } }
                              sx={ {
                                width: `100%`,
                                '& .MuiInputBase-input': { py: 0.75 },
                              } }
                            />
                            <TextField
                              type={ `number` }
                              size={ `small` }
                              value={ toDecimal(currentInstalment?.amountCents || 0) || `` }
                              onChange={ e => {
                                const nextAmount = toCents(Number(e.target.value));

                                updateAdjustedInstalment(idx, {
                                  amountCents: nextAmount || 0,
                                }, `amount`);
                              } }
                              slotProps={ { htmlInput: { min: 0, step: 0.01, 'data-adjusted-instalment-field': `amount-${idx}` } } }
                              sx={ {
                                width: `100%`,
                                '& .MuiInputBase-input': { py: 0.75, textAlign: `right` },
                              } }
                            />
                            <TextField
                              type={ `text` }
                              size={ `small` }
                              value={ percentageValue }
                              onFocus={ () => {
                                setEditingPercentage({
                                  index: idx,
                                  value: Number.isFinite(instalmentPercentage) ? instalmentPercentage.toFixed(2) : ``,
                                });
                              } }
                              onChange={ e => {
                                const nextValue = e.target.value;
                                const nextPercentage = Number(nextValue);

                                if (Number.isFinite(nextPercentage) && nextPercentage > 100) {
                                  return;
                                }

                                setEditingPercentage({
                                  index: idx,
                                  value: nextValue,
                                });

                                if (Number.isFinite(nextPercentage) && nextValue.trim() !== `` && nextPercentage < minPercentage) {
                                  return;
                                }

                                const nextAmount = Number.isFinite(nextPercentage)
                                  ? Math.round((goalInstalmentTotalCents * nextPercentage) / 100)
                                  : (nextValue.trim() === `` ? 0 : null);

                                if (nextAmount === null) {
                                  return;
                                }

                                updateAdjustedInstalment(idx, {
                                  amountCents: Math.max(0, nextAmount),
                                }, `percentage`);
                              } }
                              onBlur={ () => {
                                const blurValue = editingPercentage?.index === idx ? editingPercentage.value : ``;
                                const blurPercentage = Number(blurValue);

                                if (Number.isFinite(blurPercentage) && blurValue.trim() !== `` && blurPercentage < minPercentage) {
                                  const minAmount = Math.round((goalInstalmentTotalCents * minPercentage) / 100);

                                  updateAdjustedInstalment(idx, {
                                    amountCents: Math.max(1, minAmount),
                                  }, `percentage`);
                                }

                                setEditingPercentage(current => (current?.index === idx ? null : current));
                              } }
                              slotProps={ {
                                htmlInput: {
                                  min: 0,
                                  step: 0.01,
                                  inputMode: `decimal`,
                                  'data-adjusted-instalment-field': `percentage-${idx}`,
                                },
                                input: {
                                  endAdornment: <InputAdornment position={ `end` }>{ `%` }</InputAdornment>,
                                },
                              } }
                              sx={ {
                                width: `100%`,
                                '& .MuiInputBase-input': { py: 0.75, pl: 1, pr: 0.5, textAlign: `right` },
                                '& .MuiInputAdornment-root': { ml: 0.5, mr: 0.5, color: `#6b7280`, fontSize: `0.9rem` },
                              } }
                            />
                          </div>
                        </When>
                        <When condition={ mode === `portal` }>
                          <>
                            <span className={ `text-gray-600` }>{ `Due ${instalment.dueDate}` }</span>
                            <span className={ `font-medium` }>{ toDecimal(instalment.amountCents).toFixed(2) }</span>
                            <span className={ `font-medium text-gray-600` }>{ `${instalmentPercentage.toFixed(2)}%` }</span>
                          </>
                        </When>
                      </div>
                    );
                  })
                }
                <When condition={ mode === `app` && !!preview?.instalments?.length }>
                  <div className={ `px-3 py-2.5 text-sm bg-gray-50/80` }>
                    <div className={ `grid w-full grid-cols-[120px_165px_130px_96px] items-center gap-2` }>
                      <span className={ `text-gray-700 font-semibold` }>{ `Total` }</span>
                      <span />
                      <div className={ `w-full text-right font-semibold text-gray-800` }>
                        { toDecimal(currentInstalmentTotalCents).toFixed(2) }
                      </div>
                      <div className={ `w-full text-right font-semibold text-gray-800` }>
                        { `${currentInstalmentTotalPercentage.toFixed(2)}%` }
                      </div>
                    </div>
                    <When condition={ hasInstalmentTotalMismatch }>
                      <div className={ `mt-1 grid w-full grid-cols-[120px_165px_130px_96px] items-center gap-2 text-xs` }>
                        <span className={ `text-gray-600 font-medium` }>{ `Goal` }</span>
                        <span />
                        <div className={ `w-full text-right font-medium text-gray-600` }>
                          { toDecimal(goalInstalmentTotalCents).toFixed(2) }
                        </div>
                        <div className={ `w-full text-right font-medium text-gray-600` }>
                          { `${goalInstalmentTotalPercentage.toFixed(2)}%` }
                        </div>
                      </div>
                      <div className={ `mt-1 grid w-full grid-cols-[120px_165px_130px_96px] items-center gap-2 text-xs` }>
                        <span className={ `text-red-600 font-medium` }>{ `Difference` }</span>
                        <span />
                        <div className={ `w-full text-right font-medium text-red-600` }>
                          { `${instalmentTotalDeltaCents >= 0 ? `+` : ``}${toDecimal(instalmentTotalDeltaCents).toFixed(2)}` }
                        </div>
                        <div className={ `w-full text-right font-medium text-red-600` }>
                          { `${instalmentTotalDeltaPercentage >= 0 ? `+` : ``}${instalmentTotalDeltaPercentage.toFixed(2)}%` }
                        </div>
                      </div>
                    </When>
                  </div>
                </When>
              </div>
              <When condition={ mode === `app` }>
                <Paragraph className={ `mt-2 text-xs text-gray-500` }>
                  { `You can adjust due dates and amounts before creating the plan.` }
                </Paragraph>
              </When>
              <When condition={ hasInvalidAdjustedInstalments }>
                <Paragraph variant={ `error` }
                  size={ `sm` }
                  className={ `mt-2` }>
                  { `Each installment must have a valid due date and amount greater than zero.` }
                </Paragraph>
              </When>
              <When condition={ !!preview?.surchargeDetail }>
                <Paragraph className={ `mt-2 text-sm text-gray-600 w-full text-right` }>
                  { preview?.surchargeDetail?.description + ` included.` }
                </Paragraph>
              </When>
            </div>
          </LoadingOverlay>
        </When>

        <div className={ `flex justify-end gap-2 pt-4` }>
          <Button
            color={ `gray` }
            onClick={ onClose }
          >
            { `Cancel` }
          </Button>

          <When condition={ !preview }>
            <Button
              onClick={ () => onPreview(terms) }
              disabled={ invalid || isLoading }
            >
              { `Preview` }
            </Button>
          </When>

          <When condition={ !!preview }>
            <Button
              onClick={ onCreate }
              loading={ isLoading }
              disabled={ invalid || isLoading || hasInvalidAdjustedInstalments || hasInstalmentTotalMismatch }
              color={ `paidniceGreen` }
            >
              { `Create Plan` }
            </Button>
          </When>
        </div>
      </div>
    </Modal>
  );
}
